import type { BoxProps } from '@mui/material/Box';
import { varFade, MotionViewport } from '@/components/animate';
import { Carousel, useCarousel, CarouselDotButtons } from '@/components/carousel';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { CarouselAlignSection } from '@/types/sections/CarouselAlignSection';
import { useRef, useEffect } from 'react';
import { Button, Stack, useTheme } from '@mui/material';
import { CardApplicationsItem } from '@/types/CardApplicationsItem';
import { Icon } from '@/components/Icon';
import { getIconName } from '@/components/Icon/Icon';
import { getCtaUrl } from '@/types/Cta';

type CarouselAlignProps = BoxProps & {
  section: CarouselAlignSection;
};

export default function CarouselAlign({ sx, section, ...other }: CarouselAlignProps) {
  const carousel = useCarousel({ align: 'start', containScroll: false, slidesToShow: { xs: '100%', sm: '70%' }, slideSpacing: '50px' });
  const carouselRef = useRef<HTMLDivElement>(null);

  // Function to equalize heights
  useEffect(() => {
    if (!carouselRef.current) return;
    
    const equalizeHeights = () => {
      const carouselItems = carouselRef.current?.querySelectorAll('.carousel-item');
      if (!carouselItems?.length) return;
      
      // Reset heights to auto first to get natural heights
      carouselItems.forEach((item) => {
        (item as HTMLElement).style.height = 'auto';
      });
      
      // Find the tallest item
      let maxHeight = 0;
      carouselItems.forEach((item) => {
        maxHeight = Math.max(maxHeight, (item as HTMLElement).offsetHeight);
      });
      
      // Apply the height to all items
      if (maxHeight > 0) {
        carouselItems.forEach((item) => {
          (item as HTMLElement).style.height = `${maxHeight}px`;
        });
      }
    };
    
    // Run on initial render and window resize
    equalizeHeights();
    window.addEventListener('resize', equalizeHeights);
    
    return () => {
      window.removeEventListener('resize', equalizeHeights);
    };
  }, [section]);

  return (
    <m.div variants={varFade('inUp')}>
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 2, md: 10 } }}>
        
        {section?.small && (
          <m.div variants={varFade('inDown')}>
            <Typography variant="overline" sx={{ color: 'text.disabled' }}>
              {section?.small}
            </Typography>
          </m.div>
        )}

        { section?.title && (
          <m.div variants={varFade('inUp')}>
            <Typography variant="h2" align='left' sx={{ pb: { xs: 2, md: 4 }, color: 'primary.main' }}>
              {section?.title}
            </Typography>
          </m.div>
        )}

        { section?.subtitle && ( 
          <m.div variants={varFade('inUp')}>
            <Typography sx={{ mx: 'auto', maxWidth: 640, color: 'text.secondary' }}>
              {section?.subtitle}
            </Typography>
          </m.div>
        )}

        <m.div variants={varFade('inUp')}>
          <Stack
            flex={1}
            spacing={2} // Space between items
            flexDirection="row"
            alignItems="center"
            flexWrap="wrap" // Allows wrapping to multiple rows on smaller screens
            sx={{
              gap: 2, // Adjust the gap between items
              pb: { xs: 2, md: 4 }
            }}
          >
            {section.alloptions && section.alloptions.length > 0&&
              section.alloptions.map((option, index) => (
                <Stack
                  key={`alloptions-${index}`}
                  direction="row" // Align icon and text vertically for each item
                  alignItems="center"
                  spacing={1} // Space between icon and text
                  sx={{
                    width: { sm: 'auto' }, 
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                    }}
                  >
                    <Icon name={getIconName(option.icon ?? '', 'unknown')} size='16' color="#FFFFFF" />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      textAlign: 'center', // Center-align text
                    }}
                  >
                    {option.title}
                  </Typography>
                </Stack>
              ))}
          </Stack>
        </m.div>

        <Box sx={{ position: 'relative', alignItems: 'center' }} ref={carouselRef}>
          <Carousel carousel={carousel}>
            {section?.cards?.map((item, index) => (
              <CarouselItem 
                key={`carousel-cards-item-${index}`} 
                index={index} 
                item={{
                  id: `carousel-item-${index}`,
                  title: item.title,
                  subtitle: item.subtitle,
                  image: item.image,
                  cta1: item.cta1,
                  options: item.options,
                }}
              />
            ))}
          </Carousel>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'end',
              flexDirection: 'column',
              bgcolor: 'background.neutral',
            }}
          >
            <CarouselDotButtons
              variant="rounded"
              scrollSnaps={carousel.dots.scrollSnaps}
              selectedIndex={carousel.dots.selectedIndex}
              onClickDot={carousel.dots.onClickDot}
              sx={{ color: 'primary.main' }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
    </m.div>
  );
}

// ----------------------------------------------------------------------

type CarouselItemProps = {
  index: number;
  item: { id: string } & CardApplicationsItem;
};

function CarouselItem({ item, index }: CarouselItemProps) {
  const theme = useTheme();

  return (
    <Box 
      className="carousel-item"
      sx={{ 
        borderRadius: 0, 
        overflow: 'hidden', 
        position: 'relative', 
        textAlign: 'start',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ flex: '1 0 auto' }}>
        <Box
          paddingTop={6}
          paddingBottom={4}
          component="img"
          alt={item.title}
          src={item.image?.url || ''}
          sx={{ 
            aspectRatio: '16/10', 
            objectFit: 'cover',
            width: '100%'
          }}
        />
        <Stack key={item.id} flex={1} spacing={2} flexDirection="row" alignItems="center">
          {item.options && item.options.map((option, idx) => (
            <Box
              key={idx} // Ensure each child has a unique key
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
              }}
            >
              <Icon name={getIconName(option.icon ?? '', 'unknown')} size='16' color="#FFFFFF" />
            </Box>
          ))}
        </Stack>
        <Typography variant="h3" sx={{ my: 3, color: 'primary.main' }}>
          {item.title}
        </Typography>

        <Typography variant="body1" sx={{ maxWidth: 640, color: 'text.secondary' }}>
          {item.subtitle}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start', // Aligns content to the left
            marginTop: 'auto',
          }}>
          {item.cta1 && (
            <Button size="medium" variant="contained" color="primary" href={getCtaUrl(item.cta1)}
              sx={{ 
                my: 2,
                borderRadius: 0,
              }}
              startIcon={ item.cta1.icon && (
                  <Icon name={getIconName(item.cta1.icon, 'unknown')} size='16' color={theme.palette.common.white} />
                )}
              >
              {item?.cta1.title}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
